import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/stdntreg.css';
import { Mail, Phone, User, Lock, AlertCircle, Search, Trash2 } from 'lucide-react';

const TeacherPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    phone: ''
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchUsername, setSearchUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch username suggestions when search input changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchUsername.length >= 2) {
        try {
          const response = await axios.get(`https://gnrl-bknd-server.onrender.com/api/users/suggestions/${searchUsername}`);
          setSuggestions(response.data.suggestions);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchUsername]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchInputChange = (e) => {
    setSearchUsername(e.target.value);
  };

  const handleSuggestionClick = (username) => {
    setSearchUsername(username);
    setShowSuggestions(false);
    handleSearch(username);
  };

  const handleSearch = async (usernameToSearch = searchUsername) => {
    if (!usernameToSearch) {
      setMessage({ text: 'Please enter a username to search', type: 'error' });
      return;
    }

    try {
      console.log('Searching for user:', usernameToSearch);
      const response = await axios.get(`https://gnrl-bknd-server.onrender.com/api/users/username/${usernameToSearch}`);
      
      if (response.data.user) {
        const userData = response.data.user;
        console.log('Found user data:', userData);
        
        setFormData({
          username: userData.username,
          password: userData.password,
          email: userData.email,
          phone: userData.phone,
        });
        setOriginalUsername(userData.username);
        setIsEditMode(true);
        setMessage({ text: 'User found! You can now edit their details.', type: 'success' });
      } else {
        setMessage({ text: 'User not found', type: 'error' });
      }
    } catch (error) {
      console.error('Error searching for user:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Error searching for user', 
        type: 'error' 
      });
    }
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (isEditMode) {
        // Update existing user
        response = await axios.put(`https://gnrl-bknd-server.onrender.com/api/users/username/${originalUsername}`, formData);
      } else {
        // Register new user
        response = await axios.post('https://gnrl-bknd-server.onrender.com/api/register', formData);
      }
      
      setMessage({ 
        text: isEditMode ? 'User details updated successfully' : response.data.message, 
        type: 'success' 
      });
      
      // Clear form after successful submission
      if (!isEditMode) {
        setFormData({
          username: '',
          password: '',
          email: '',
          phone: ''
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Error in processing request', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      email: '',
      phone: ''
    });
    setIsEditMode(false);
    setSearchUsername('');
    setOriginalUsername('');
    setMessage({ text: '', type: '' });
  };

  const handleDelete = async () => {
    if (!originalUsername) {
      setMessage({ text: 'No user selected for deletion', type: 'error' });
      return;
    }

    // Confirm deletion
    const confirmDelete = window.confirm(`Are you sure you want to delete the user "${originalUsername}" `);
    if (!confirmDelete) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.delete(`https://gnrl-bknd-server.onrender.com/api/users/username/${originalUsername}`);
      
      setMessage({ 
        text: response.data.message, 
        type: 'success' 
      });
      
      // Reset form after successful deletion
      resetForm();
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Error deleting user', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-container">
      <div className="form-wrapper">
        <img src='cmpnylogo.jpg' alt="Immersive Digital Solutions" className="logo" />
        <h1>{isEditMode ? 'Edit Student Details' : 'Student Registration'}</h1>

        {/* Search Section */}
        <div className="search-section">
          <div className="search-container">
            <div className="search-input-container">
              <input
                type="text"
                value={searchUsername}
                onChange={handleSearchInputChange}
                onFocus={() => searchUsername.length >= 2 && setShowSuggestions(true)}
                placeholder="Search by username"
                className="search-input"
                autoComplete="off"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="suggestions-list">
                  {suggestions.map((suggestion, index) => (
                    <li 
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="suggestion-item"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button 
              type="button"
              onClick={() => handleSearch()}
              className="search-button"
              disabled={loading}
            >
              <Search className="icon" />
              Search
            </button>
          </div>
        </div>
        
        {message.text && (
          <div 
            className={`message ${message.type}`}
            role="alert"
            aria-live="polite"
          >
            {message.type === 'error' && <AlertCircle className="message-icon" />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="username">
              <User className="icon" />
              <span>Username</span>
            </label>
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Choose a username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <Lock className="icon" />
              <span>Password</span>
            </label>
            <input
              id="password"
              type="text"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={!isEditMode}
              className="form-input"
              placeholder={isEditMode ? "Current password shown" : "Create a strong password"}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <Mail className="icon" />
              <span>Email</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter student email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              <Phone className="icon" />
              <span>Phone</span>
            </label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter 10-digit phone number"
            />
          </div>

          <div className="button-group">
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : isEditMode ? 'Update Student' : 'Register Student'}
            </button>

            {isEditMode && (
              <>
                <button 
                  type="button" 
                  className="reset-button"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel Edit
                </button>
                <button 
                  type="button" 
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={loading}
                  aria-label="Delete Student"
                  title="Delete Student"
                >
                  <Trash2 className="icon" />
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherPage;
